import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from '../utils/toastPolicy';
import { buildSwitchSummary } from '../utils/branchSwitch';
import {
  MapPin, ChevronDown, ArrowRight,
  Coffee, CheckCircle, Clock, Star, Plus, Loader2
} from 'lucide-react';
import NotificationBell from '../components/NotificationBell';
import { FreeCoffeeProgress } from '../components/FreeCoffeeProgress';
import WelcomeCoffeeCard from '../components/WelcomeCoffeeCard';
import ReadyPickupPopup from '../components/ReadyPickupPopup';
import RenewPassPopup from '../components/RenewPassPopup';
import LaunchPopup from '../components/LaunchPopup';
import FloatingMenuButton from '../components/FloatingMenuButton';
import OptimizedImage, { warmOptimizedImage } from '../components/OptimizedImage';
import BannerSlot from '../components/BannerSlot';
import TiffinOfTheDayBar from '../components/TiffinOfTheDayBar';
import MenuSearchAutocomplete from '../components/MenuSearchAutocomplete';
import ReviewPrompt from '../components/ReviewPrompt';
import ReviewDetailSheet from '../components/ReviewDetailSheet';
import { LandingMenuSkeleton } from '../components/PageSkeletons';
import { getCurrentPosition, isGeolocationAvailable } from '../utils/geolocation';
import { findPromptableOrder } from '../utils/orderStatus';
import { readCache, writeCache, CACHE_KEYS } from '../utils/menuCache';
import { trackDiscovery } from '../utils/analytics';
import { isNativeApp } from '../utils/nativePayment';
import { storeStatus as computeStoreStatus } from '../utils/storeHours';
import { orderCategories, homeRowsFor, menuCategoryNames } from '../utils/menuSections';
import { useMenuLayout } from '../hooks/useMenuLayout';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { FestiveLanterns, FestiveMandala, FestiveMarigolds, FestiveGreeting } from '../components/FestiveDecor';

// Light haptic on native, no-op everywhere else. Same runtime-bridge pattern as
// BottomNav's tapHaptic / Menu's lightHaptic — no @capacitor/haptics dep, so this
// works pre-AAB too.
function lightHaptic() {
  try {
    const impact = window?.Capacitor?.Plugins?.Haptics?.impact;
    if (typeof impact === 'function') impact({ style: 'LIGHT' });
  } catch { /* noop */ }
}

// "From the World" spotlight — fixed lineup of international classics, in this
// display order. Matched against menu item names with the "(Iced)" / "(Hot)"
// suffix normalized away so "Vietnamese Cold Coffee (Iced)" matches; a branch
// that doesn't carry an item just skips it. flag/origin feed the country chip
// on each card ("Espresso Atlas" panel treatment).
const FROM_THE_WORLD_LINEUP = [
  { key: 'vietnamese cold coffee', flag: '🇻🇳', origin: 'Vietnam' },
  { key: 'french vanilla latte', flag: '🇫🇷', origin: 'France' },
  { key: 'iced spanish latte', flag: '🇪🇸', origin: 'Spain' },
  { key: 'hot spanish latte', flag: '🇪🇸', origin: 'Spain' },
  { key: 'classic matcha latte', flag: '🇯🇵', origin: 'Japan' },
  { key: 'flat white', flag: '🇦🇺', origin: 'Australia' },
];

function spotlightNameKey(name) {
  return (name || '').toLowerCase().replace(/\s*\((iced|hot)\)\s*$/, '').trim();
}

// Decorative wireframe globe bleeding off the panel's top-right corner.
function WorldGlobeSketch() {
  return (
    <svg
      aria-hidden="true"
      className="pointer-events-none absolute -right-[70px] -top-[40px] h-[230px] w-[230px] opacity-50"
      viewBox="0 0 200 200" fill="none" stroke="hsl(35 45% 88%)" strokeWidth="1"
    >
      <circle cx="100" cy="100" r="96" />
      <ellipse cx="100" cy="100" rx="96" ry="38" />
      <ellipse cx="100" cy="100" rx="96" ry="70" />
      <ellipse cx="100" cy="100" rx="38" ry="96" />
      <ellipse cx="100" cy="100" rx="70" ry="96" />
      <line x1="4" y1="100" x2="196" y2="100" />
    </svg>
  );
}

// Dashed flight-route arc + waypoint dots + paper plane across the panel head.
// The dash drift animation (.pb-route-path) lives in index.css, reduced-motion
// gated there.
function WorldRouteSketch() {
  return (
    <svg
      aria-hidden="true"
      className="pointer-events-none absolute inset-x-0 top-0 h-[190px] w-full"
      viewBox="0 0 400 190" preserveAspectRatio="none"
    >
      <path
        className="pb-route-path"
        d="M -10 128 C 90 58, 200 40, 300 62 S 420 108, 430 96"
        fill="none" stroke="hsl(25 90% 50% / 0.55)" strokeWidth="1.6"
        strokeDasharray="7 9" strokeLinecap="round"
      />
      <circle cx="52" cy="93" r="3" fill="hsl(25 90% 55%)" />
      <circle cx="168" cy="47" r="3" fill="hsl(25 90% 55%)" />
      <circle cx="300" cy="62" r="3" fill="hsl(25 90% 55%)" />
      <g transform="translate(360 78) rotate(18)">
        <path d="M0 0 L-11 4 L-8 0 L-11 -4 Z" fill="hsl(35 45% 90%)" />
      </g>
    </svg>
  );
}

// One spotlight card. `world` flips the Espresso Atlas treatment: borderless
// card floating on the dark panel + the origin flag chip over the photo.
// Transactional internals (price, discount math, Add affordance, sheet
// navigation) are identical in both modes.
function SpotlightCard({ entry, location, world = false, onTap }) {
  const item = entry.item;
  return (
    <Link
      to={`/menu/item/${item.id}`}
      state={{ backgroundLocation: location }}
      onClick={onTap}
      className="flex-shrink-0 w-44 max-w-[80vw]"
      data-testid={`spotlight-item-${item.id}`}
    >
      <Card className={world ? 'overflow-hidden border-0 shadow-[0_6px_18px_rgba(0,0,0,0.35)]' : 'overflow-hidden'}>
        <div className="relative h-36 bg-muted overflow-hidden">
          {world && entry.origin && (
            <span className="absolute left-2 top-2 z-10 inline-flex items-center gap-1 rounded-full bg-[hsl(25_45%_10%/0.72)] px-2 py-1 text-[10px] font-extrabold uppercase tracking-wider text-[hsl(35_45%_92%)] backdrop-blur-sm">
              {entry.flag} {entry.origin}
            </span>
          )}
          <OptimizedImage
            src={item.image_url}
            imageUrls={item.image_urls}
            size="thumb"
            alt={item.name}
            className="w-full h-full"
            width={176}
            height={144}
            eager
          />
        </div>
        <div className="p-2.5">
          <p className="text-xs font-semibold line-clamp-2 leading-tight min-h-[2rem]">{item.name}</p>
          <div className="flex items-center justify-between mt-1">
            {item.item_discount ? (
              <div className="flex items-center gap-1">
                <p className="text-sm font-bold text-green-600">
                  ₹{item.item_discount.discount_type === 'percentage'
                    ? Math.round(item.price * (1 - item.item_discount.discount_value / 100))
                    : Math.max(0, item.price - item.item_discount.discount_value)}
                </p>
                <p className="text-[10px] text-muted-foreground line-through">₹{item.price}</p>
              </div>
            ) : (
              <p className="text-sm font-bold text-foreground">₹{item.price}</p>
            )}
            <span className="text-[10px] font-semibold text-accent flex items-center gap-0.5">
              <Plus className="w-3 h-3" /> Add
            </span>
          </div>
        </div>
      </Card>
    </Link>
  );
}

// Read the saved branch id once at module scope so lazy useState initializers
// can key the menu cache without each one re-parsing localStorage.
function getSavedBranchIdSync() {
  try {
    const saved = typeof window !== 'undefined' && window.localStorage
      ? window.localStorage.getItem('selectedBranch')
      : null;
    return saved ? JSON.parse(saved)?.id || null : null;
  } catch {
    return null;
  }
}

function getSavedBranchSync() {
  try {
    const saved = typeof window !== 'undefined' && window.localStorage
      ? window.localStorage.getItem('selectedBranch')
      : null;
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
}

// Read a branch's menu from the build-time __INITIAL_DATA__ script tag (injected
// by scripts/prerender-shell.js). Used as a fallback by the lazy useState
// initializers — preferred order is SWR cache (most recent) > inline build-time
// data > empty. Inline data is build-time-frozen and may be hours stale; the
// live /menu fetch refreshes immediately after mount and the backend re-validates
// pricing at order time (Business Rule #27). Returns null on miss / parse error.
function getInlineMenuSync(branchId) {
  if (!branchId) return null;
  try {
    if (typeof document === 'undefined') return null;
    const el = document.getElementById('__INITIAL_DATA__');
    if (!el) return null;
    const data = JSON.parse(el.textContent);
    const menu = data?.menus?.[branchId];
    return Array.isArray(menu) && menu.length > 0 ? menu : null;
  } catch {
    return null;
  }
}

// Header layout A/B toggle. true = store-prominent (store name bold on top, distance + cold-start highlight);
// false = original (greeting bold on top, branch selector as the small line below). Flip to compare.
const STORE_PROMINENT_HEADER = true;

// Business Rule #37 — post-order taste capture toggle. When false, the
// post-delivery rating flow (ReviewPrompt → ReviewDetailSheet) is PLAIN feedback
// only (stars + per-product thumbs + free text), like before BR#37 — the taste
// questions are NOT folded in. Taste is captured on the CART instead. Flip to
// true to re-enable the post-order taste questions (the prompt-eligibility fetch
// + auto-open both gate on this).
const POST_ORDER_TASTE_CAPTURE = false;

// Branch detection only needs CITY-level accuracy (the branches are ~4–15 km
// apart), so we use coarse/network location, NOT GPS. `enableHighAccuracy:true`
// forced a cold GPS fix that took 10–30s on first install (often hitting the 10s
// timeout and failing); coarse returns in ~1–2s even cold. `maximumAge:60000`
// lets a rapid relaunch reuse a <1-min-old fix without re-acquiring, but keeps it
// fresh enough that a real move is re-detected on the next cold start.
const GEO_DETECT_OPTS = { enableHighAccuracy: false, timeout: 6000, maximumAge: 60000 };
// Coarse fixes are less precise than GPS — accept up to 3 km accuracy (still
// unambiguous for branches 4–15 km apart). The old 1 km cap rejected most coarse
// fixes outright, which would have defeated the speed fix.
const MAX_DETECT_ACCURACY_M = 3000;
// 2-min throttle on resume re-checks so quick app-switches don't re-locate.
const RESUME_THROTTLE_MS = 120000;

/**
 * Home page; orchestrates branch selection (URL → localStorage → GPS), menu fetch, and all landing sections.
 */
export default function Landing() {
  const { user, token } = useAuth();
  const { addToCart, fetchCart, switchBranch, cart } = useCart();
  const navigate = useNavigate();
  // Captured for ProductDetail navigation: passes `backgroundLocation` so the
  // sheet renders OVER this Landing page (Menu scroll preservation pattern in
  // App.js). When user dismisses the sheet they return here, not to /menu.
  const location = useLocation();
  // Admin-arranged menu layout: drives the Categories grid order + quick-link rows.
  const { sections: layoutSections, homeRows: layoutHomeRows } = useMenuLayout();
  const [searchParams] = useSearchParams();

  // Lazy initializers hydrate from the SWR cache (utils/menuCache) so a
  // returning customer paints last-seen content on the first React render
  // instead of waiting for ~6 parallel API calls to resolve. The mount-time
  // useEffect still fires every fetcher to refresh in the background; setState
  // there swaps stale → fresh once the network lands. See menuCache.js for the
  // 24h TTL + schema-version contract that gates a hit.
  const [branches, setBranches] = useState(() => readCache(CACHE_KEYS.branches) || []);
  const [selectedBranch, setSelectedBranch] = useState(() => getSavedBranchSync());
  const [showBranchPicker, setShowBranchPicker] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [menuItems, setMenuItems] = useState(() => {
    const branchId = getSavedBranchIdSync();
    if (!branchId) return [];
    return readCache(CACHE_KEYS.menu(branchId)) || getInlineMenuSync(branchId) || [];
  });
  const [comboOffers, setComboOffers] = useState(() => readCache(CACHE_KEYS.comboOffers) || []);
  const [subscriptions, setSubscriptions] = useState(() => readCache(CACHE_KEYS.subscriptions) || []);
  const [recentOrders, setRecentOrders] = useState(() => (user?.id ? readCache(CACHE_KEYS.recentOrders(user.id)) : null) || []);
  const [reorderLoading, setReorderLoading] = useState(null);
  const [categories, setCategories] = useState(() => {
    const branchId = getSavedBranchIdSync();
    if (!branchId) return [];
    const source = readCache(CACHE_KEYS.menu(branchId)) || getInlineMenuSync(branchId);
    return source ? [...new Set(source.map((i) => i.category))] : [];
  });
  const [categoryImages, setCategoryImages] = useState(() => readCache(CACHE_KEYS.categoryImages) || {});
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const [pendingBranches, setPendingBranches] = useState([]);
  const [outsideCity, setOutsideCity] = useState(false);
  // Resume re-detection (Fix 2): when the app is foregrounded after the user has
  // moved closer to a different branch, suggest switching via a modal (never auto-
  // switch — selectBranch clears the cart). `switchSuggestion` = {branch, distanceKm}.
  const [switchSuggestion, setSwitchSuggestion] = useState(null);
  const lastResumeCheckRef = useRef(Date.now()); // throttle resume re-checks
  const dismissedSwitchRef = useRef(new Set());   // branch ids declined this session
  const checkResumeRef = useRef(null);            // always points at the latest checkBranchOnResume
  // Native first-launch: show the "Finding your nearest store…" detecting card
  // (NOT the branch picker) until the OS location dialog + GPS detection conclude.
  // Lazy-init so the very first paint already shows the detecting card (no hero flash).
  const [detectingLocation, setDetectingLocation] = useState(
    () => isNativeApp() && !getSavedBranchSync() && !(typeof sessionStorage !== 'undefined' && sessionStorage.getItem('branchAutoDetected'))
  );
  // True only when the customer holds an active pass with the per-pass
  // hide_free_coffee_tracker flag set (Business Rule #23 rework, pt 7).
  const [hideFreeCoffeeTracker, setHideFreeCoffeeTracker] = useState(false);
  const [userCoords, setUserCoords] = useState(null);
  // Cold-start one-time inline highlight on the header store line (not a toast).
  const [storeHighlight, setStoreHighlight] = useState(false);
  // Business Rule #19 — home-screen review prompt + detailed-feedback stages.
  const [reviewOrder, setReviewOrder] = useState(null);
  const [reviewPromptOpen, setReviewPromptOpen] = useState(false);
  const [reviewDetailOpen, setReviewDetailOpen] = useState(false);
  const [reviewDetailRating, setReviewDetailRating] = useState(0);
  // Business Rule #37 — taste-personalization questions ride inside the rating flow.
  const [personalizationAsk, setPersonalizationAsk] = useState([]);
  const [personalizationConsent, setPersonalizationConsent] = useState({ required: false, version: 'v1.0' });

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const branchRef = useRef(null);
  const splashDismissed = useRef(false);

  // Business Rule #38 — live store availability for the "closed / not operational"
  // home banner. Seeded from the stored branch (client mirror) then corrected by
  // GET /branches/:id/status (the authority for the exact "opens at" label).
  const [storeState, setStoreState] = useState(() => {
    const b = getSavedBranchSync();
    return b ? computeStoreStatus(b) : null;
  });
  useEffect(() => {
    if (!selectedBranch?.id) { setStoreState(null); return undefined; }
    let cancelled = false;
    setStoreState(computeStoreStatus(selectedBranch)); // instant paint from the picked branch
    axios.get(`${API}/branches/${selectedBranch.id}/status`)
      .then(res => { if (!cancelled) setStoreState(res.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selectedBranch?.id, API]); // eslint-disable-line react-hooks/exhaustive-deps
  const storeClosed = !user?.is_admin && storeState && storeState.open === false;

  // Dismiss splash when the page has something to render — either the menu
  // (branch + categories loaded) or the ChooseStoreHero (branches loaded but
  // no branch picked). Without the second condition the splash would never
  // dismiss for a first-time visitor who skips/denies location.
  useEffect(function dismissSplashWhenReady() {
    if (splashDismissed.current) return undefined;
    const menuReady = selectedBranch && categories.length > 0;
    const chooseStoreReady = !selectedBranch && branches.length > 0;
    if (!menuReady && !chooseStoreReady) return undefined;
    const timer = setTimeout(function fireSplashReady() {
      splashDismissed.current = true;
      window.dispatchEvent(new Event('app-ready'));
    }, 200);
    return function cleanup() { clearTimeout(timer); };
  }, [selectedBranch, categories, branches]);

  // Idle prewarm of the branch's MENU-LIST thumbnails (native only) so the first
  // Menu open paints instantly instead of blur-loading each row. Throttled and
  // low-priority (warmOptimizedImage sets fetchPriority:'low') so it never
  // competes with what's on screen: starts after Home settles, warms in small
  // batches. Same { size:'thumb', sizes:'128px' } the Menu card uses, so the
  // browser fetches the exact `medium` variant the card will request → a real
  // cache hit. Web keeps plain lazy-load (no extra launch data).
  useEffect(() => {
    if (!isNativeApp() || !menuItems || menuItems.length === 0) return undefined;
    const items = menuItems.filter(i => i.image_urls || i.image_url);
    if (!items.length) return undefined;
    let cancelled = false;
    let idx = 0;
    const BATCH = 5;
    const warmBatch = () => {
      if (cancelled) return;
      items.slice(idx, idx + BATCH).forEach(i =>
        warmOptimizedImage({ src: i.image_url, imageUrls: i.image_urls, size: 'thumb', sizes: '128px' })
      );
      idx += BATCH;
      if (idx < items.length) setTimeout(warmBatch, 250);
    };
    const ric = typeof window !== 'undefined' ? window.requestIdleCallback : null;
    const startId = ric ? ric(warmBatch, { timeout: 4000 }) : setTimeout(warmBatch, 2000);
    return () => {
      cancelled = true;
      if (ric && window.cancelIdleCallback) window.cancelIdleCallback(startId);
      else clearTimeout(startId);
    };
  }, [menuItems]);

  useEffect(() => {
    fetchBranches();
    fetchComboOffers();
    fetchSubscriptions();
    fetchCategoryImages();
    if (user) {
      fetchRecentOrders();
      fetchUserSubscription();
    }
  }, [user]);

  // Fix 2 — re-check the nearest branch when the app returns to the foreground
  // (native only). Bound once; calls the latest closure via `checkResumeRef`.
  // 2-minute throttle so quick app-switches don't re-locate; `lastResumeCheckRef`
  // is seeded at mount so this never double-fires right after the initial detect.
  useEffect(() => {
    if (!isNativeApp()) return undefined;
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastResumeCheckRef.current < RESUME_THROTTLE_MS) return;
      lastResumeCheckRef.current = now;
      checkResumeRef.current?.();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  // Retry branch auto-detection after the user grants location permission.
  // useNativeShellInit dispatches this event when the system dialog resolves to granted.
  useEffect(() => {
    const handler = () => {
      if (!sessionStorage.getItem('branchAutoDetected') && branches.length > 0) {
        runAutoDetection(branches);
      }
    };
    window.addEventListener('pb-location-granted', handler);
    return () => window.removeEventListener('pb-location-granted', handler);
  }, [branches]); // re-register when branches loads so handler has the full list

  // Cold-start store confirmation: briefly highlight the header store line ONCE
  // per app launch, but only once the store AND its distance are both resolved
  // (so the pulse never shows a store with no distance). Slow pb-store-attention
  // pulse ×3 then stops. Gated by a sessionStorage flag that clears on app close
  // (native cold start / web tab close), so it re-fires on a genuine reopen but
  // never on in-app navigation/re-render.
  useEffect(() => {
    if (!selectedBranch || !userCoords) return undefined;
    if (!selectedBranch.latitude || !selectedBranch.longitude) return undefined;
    if (sessionStorage.getItem('pb-store-confirm-shown')) return undefined;
    sessionStorage.setItem('pb-store-confirm-shown', '1');
    setStoreHighlight(true);
    const timer = setTimeout(() => setStoreHighlight(false), 3500);
    return () => clearTimeout(timer);
  }, [selectedBranch, userCoords]);

  // Refetch menu when branch changes
  useEffect(() => {
    if (selectedBranch) {
      fetchMenuItems(selectedBranch.id);
    }
  }, [selectedBranch]);

  // Surface the home-screen review prompt when a rateable, non-dismissed order
  // is in the recent-orders payload (Business Rule #19). Defer slightly so the
  // sheet doesn't fight the splash dismiss / location prompt for first paint.
  useEffect(() => {
    if (!user) return undefined;
    if (reviewPromptOpen || reviewDetailOpen) return undefined;
    if (showLocationPrompt) return undefined;
    const candidate = findPromptableOrder(recentOrders);
    if (!candidate) return undefined;
    if (reviewOrder?.id === candidate.id) return undefined;
    const timer = setTimeout(() => {
      setReviewOrder(candidate);
      setReviewPromptOpen(true);
    }, 1200);
    return () => clearTimeout(timer);
  }, [user, recentOrders, showLocationPrompt, reviewPromptOpen, reviewDetailOpen, reviewOrder?.id]);

  // Business Rule #37 — fetch which taste dimensions to ask, SCOPED to the order
  // being rated (so ordering a Cappuccino only asks the Cappuccino's enabled
  // questions). Keyed on the review order; no-op when the feature is off or
  // nothing is due (empty `ask`).
  useEffect(() => {
    // Post-order taste capture is disabled for now (cart-only) — keep the
    // rating flow plain. Flip POST_ORDER_TASTE_CAPTURE to re-enable.
    if (!POST_ORDER_TASTE_CAPTURE || !user || !token || !reviewOrder?.id) { setPersonalizationAsk([]); return; }
    let cancelled = false;
    axios
      .get(`${API}/personalization/prompt-eligibility`, {
        params: { order_id: reviewOrder.id },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (cancelled) return;
        setPersonalizationAsk(Array.isArray(res.data?.ask) ? res.data.ask : []);
        setPersonalizationConsent({
          required: !!res.data?.consent_required,
          version: res.data?.consent_version || 'v1.0',
        });
      })
      .catch(() => { if (!cancelled) setPersonalizationAsk([]); });
    return () => { cancelled = true; };
  }, [user, token, reviewOrder?.id]);

  // Close branch picker on outside click
  useEffect(() => {
    const handler = (e) => {
      if (branchRef.current && !branchRef.current.contains(e.target)) setShowBranchPicker(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Read pre-rendered initial data (injected at build time)
  const getInitialData = () => {
    try {
      const el = document.getElementById('__INITIAL_DATA__');
      if (el) return JSON.parse(el.textContent);
    } catch {}
    return null;
  };

  const fetchBranches = async () => {
    try {
      // Use inline data if available (avoids network round-trip)
      const initial = getInitialData();
      let branchData;
      if (initial?.branches) {
        branchData = initial.branches;
      } else {
        // Send the token when present so an ADMIN additionally receives admin_only
        // test branches in the picker (the backend gates by is_admin). A normal
        // customer's response is identical to the public list.
        const authToken = localStorage.getItem('token');
        const cfg = authToken ? { headers: { Authorization: `Bearer ${authToken}` } } : undefined;
        branchData = (await axios.get(`${API}/branches`, cfg)).data;
        // Cache only the public-equivalent list — never an admin's (which carries
        // the admin_only test branch) so a logged-out session on the same device
        // can't surface it in the picker. The backend hard-gates selection/use
        // regardless; this just keeps the cached picker clean.
        if (!user?.is_admin) writeCache(CACHE_KEYS.branches, branchData);
      }
      setBranches(branchData);

      if (branchData.length === 0) return;

      // Load category images from inline data or API
      if (initial?.categoryImages && Object.keys(initial.categoryImages).length > 0) {
        setCategoryImages(initial.categoryImages);
        writeCache(CACHE_KEYS.categoryImages, initial.categoryImages);
      }

      // Priority 1: URL ?branch=ID deep-link (from QR code)
      const urlBranchId = searchParams.get('branch');
      if (urlBranchId) {
        let urlMatch = branchData.find(b => b.id === urlBranchId);
        // Customer-hidden branches are excluded from GET /branches, so a QR /
        // deep link to an internal test branch won't be in branchData — fetch it
        // directly by id (the single-branch endpoint isn't visibility-filtered).
        // This is the access path for a customer-hidden test branch. Gate on
        // is_active so a branch switched OFF as "not in operation" can't be
        // ordered from even via an old link — only a hidden-but-operational
        // (test) branch resolves here.
        if (!urlMatch) {
          try {
            // Send the token so an admin can deep-link to an admin_only test branch
            // (GET /branches/{id} returns it only for is_admin; a non-admin gets a
            // 404 here, so the branch simply isn't selected — "blocked even via link").
            const authToken = localStorage.getItem('token');
            const cfg = authToken ? { headers: { Authorization: `Bearer ${authToken}` } } : undefined;
            const direct = (await axios.get(`${API}/branches/${urlBranchId}`, cfg)).data;
            if (direct?.id && direct.is_active !== false) urlMatch = direct;
          } catch {}
        }
        if (urlMatch) {
          selectBranch(urlMatch);
          sessionStorage.setItem('branchManuallySelected', 'true');
          toast.locationStatus(`Welcome to ${urlMatch.name}!`, { duration: 3000 });
          return;
        }
      }

      // Priority 2: Restore saved branch from localStorage
      // `selectedBranch` is also lazy-init'd from localStorage so cache hits
      // paint instantly. Re-validating here either (a) refreshes the in-state
      // object with the canonical branch from the API (in case lat/lng/name
      // changed), or (b) clears the stale lazy-init value when the saved
      // branch was deleted server-side — without (b) the user would be stuck
      // on a non-existent branch with a non-recoverable broken menu fetch.
      let hasBranch = false;
      const saved = localStorage.getItem('selectedBranch');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          const match = branchData.find(b => b.id === parsed.id);
          if (match) {
            setSelectedBranch(match);
            hasBranch = true;
          } else {
            setSelectedBranch(null);
            localStorage.removeItem('selectedBranch');
          }
        } catch {}
      }

      // If no QR + no saved branch, leave selectedBranch null. The location
      // prompt (first-time visitor) or the ChooseStoreHero + picker (returning
      // visitor whose saved branch was cleared / deleted) drives the next step.
      // Never silently default to branchData[0] — Business Rule: branch
      // selection is never silent.

      // Skip if user already made a choice this session
      const manuallySelected = sessionStorage.getItem('branchManuallySelected');
      const alreadyDetected = sessionStorage.getItem('branchAutoDetected');
      if (manuallySelected || alreadyDetected) return;

      // Returning user — silently re-check nearest in case they've moved.
      // runAutoDetection toasts only when the nearest branch differs from the saved one.
      if (hasBranch) {
        runAutoDetection(branchData);
        return;
      }

      // First-time visitor. On native the OS location dialog already fired at
      // launch (useNativeShellInit) — don't stack an in-app modal on top; just
      // run auto-detection (the pb-location-granted event also retries on grant).
      // On web the in-app modal is the intentional pre-prompt before the browser
      // permission dialog. Either way selectedBranch stays null until detection
      // resolves, so ChooseStoreHero drives the manual fallback (no silent default).
      if (isNativeApp()) {
        setDetectingLocation(true);
        runAutoDetection(branchData);
        return;
      }
      setPendingBranches(branchData);
      setShowLocationPrompt(true);
    } catch {}
  };

  // Robust auto-detection: uses high-accuracy GPS for reliable results
  const runAutoDetection = async (branchList) => {
    if (!isGeolocationAvailable() || branchList.length === 0) {
      sessionStorage.setItem('branchAutoDetected', 'true');
      setDetectingLocation(false);
      return;
    }
    setDetectingLocation(true);

    const resolveNearest = async (position) => {
      const { latitude, longitude, accuracy } = position.coords;
      console.log('[Branch Detection] Coords:', { latitude, longitude, accuracy: Math.round(accuracy) + 'm' });
      setUserCoords({ latitude, longitude });

      // If accuracy is too poor, don't auto-switch — let user pick
      if (accuracy > MAX_DETECT_ACCURACY_M) {
        console.log('[Branch Detection] Accuracy too low, skipping auto-switch');
        sessionStorage.setItem('branchAutoDetected', 'true');
        return;
      }

      if (!isWithinMMR(latitude, longitude)) {
        console.log('[Branch Detection] Outside MMR bounds');
        sessionStorage.setItem('branchAutoDetected', 'true');
        // The "not delivering in your city" message is now baked into the
        // ChooseStoreHero (an in-page banner above the branch list), not a
        // competing full-screen modal — so no push-prompt sequencing is needed.
        setOutsideCity(true);
        return;
      }

      try {
        const nearestRes = await axios.get(`${API}/branches/nearest`, { params: { lat: latitude, lng: longitude } });
        const nearest = nearestRes.data;
        console.log('[Branch Detection] API response:', nearest?.name, nearest?.distance_km + 'km');
        
        if (nearest && nearest.id) {
          const match = branchList.find(b => b.id === nearest.id);
          if (match) {
            const currentId = JSON.parse(localStorage.getItem('selectedBranch') || '{}').id;
            if (currentId !== match.id) {
              selectBranch(match);
              toast.locationStatus(`Nearest store: ${match.name} (${nearest.distance_km} km)`, { duration: 3000 });
            }
            sessionStorage.setItem('branchAutoDetected', 'true');
            return;
          }
        }
      } catch (err) {
        console.error('[Branch Detection] API error:', err.message);
      }

      // Fallback: client-side haversine. Only override the saved branch when
      // at least one branch has coords — otherwise we have no signal and leave
      // the saved branch alone (no silent override, no silent default).
      let nearest = null;
      let minDist = Infinity;
      branchList.forEach(b => {
        if (b.latitude && b.longitude) {
          const d = haversineKm(latitude, longitude, b.latitude, b.longitude);
          if (d < minDist) { minDist = d; nearest = b; }
        }
      });
      if (nearest) {
        const currentId = JSON.parse(localStorage.getItem('selectedBranch') || '{}').id;
        if (currentId !== nearest.id) {
          selectBranch(nearest);
          toast.locationStatus(`Nearest store: ${nearest.name} (${minDist.toFixed(1)} km)`, { duration: 3000 });
        }
      }
      sessionStorage.setItem('branchAutoDetected', 'true');
    };

    try {
      const position = await getCurrentPosition(GEO_DETECT_OPTS);
      await resolveNearest(position);
    } catch (err) {
      console.warn('[Branch Detection] Geolocation error:', err?.code, err?.message);
      // err.code 1 = PERMISSION_DENIED — don't block future detection because
      // the system permission dialog (from useNativeShellInit) may still be open;
      // Landing will retry via the pb-location-granted event once user taps Allow
      if (err?.code !== 1) {
        sessionStorage.setItem('branchAutoDetected', 'true');
      }
    } finally {
      // Detection concluded (success / outside-MMR / low-accuracy / denied /
      // error) — drop the detecting card so ChooseStoreHero can take over when
      // no branch was selected.
      setDetectingLocation(false);
    }
  };

  // Client-side haversine for fallback
  const haversineKm = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  // Distance (km, 1 dp) from the user to the selected branch for the header
  // label. null when we lack user coords or the branch has no coordinates.
  const headerDistanceKm = (selectedBranch && userCoords && selectedBranch.latitude && selectedBranch.longitude)
    ? haversineKm(userCoords.latitude, userCoords.longitude, selectedBranch.latitude, selectedBranch.longitude).toFixed(1)
    : null;

  const handleAllowLocation = () => {
    sessionStorage.setItem('branchAutoDetected', 'true');
    setShowLocationPrompt(false);
    detectNearestBranch(pendingBranches);
  };

  const handleSkipLocation = () => {
    // Customer dismissed the in-app location prompt. Open the picker and
    // leave selectedBranch null — ChooseStoreHero will force an explicit pick.
    // Suppress further silent auto-detect this session (they already declined).
    setShowLocationPrompt(false);
    setShowBranchPicker(true);
    sessionStorage.setItem('branchAutoDetected', 'true');
  };

  // Mumbai MMR bounding box (generous)
  const isWithinMMR = (lat, lng) => lat >= 18.85 && lat <= 19.45 && lng >= 72.7 && lng <= 73.2;

  // Platform-aware "location blocked" instructions. iOS Safari's per-site
  // permission lives under the AA menu (not a lock/info icon); Android Chrome
  // uses the lock icon. The generic desktop message keeps the original wording.
  const getLocationBlockedMessage = () => {
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    if (/iPad|iPhone|iPod/.test(ua)) {
      return 'Location is off for this site. Open Safari’s AA menu → Website Settings → Location → Ask, then refresh.';
    }
    if (/Android/.test(ua)) {
      return 'Location is off for this site. Tap the lock icon in the address bar → Permissions → Location → Allow, then refresh.';
    }
    return 'Location blocked. Tap the lock / info icon in your address bar → turn on Location → refresh.';
  };

  const detectNearestBranch = async (branchList) => {
    if (!isGeolocationAvailable() || branchList.length === 0) {
      // Customer tapped "Detect Nearest Store" but geolocation isn't available.
      // Surface the failure — the ChooseStoreHero is already visible (no branch
      // selected) so the customer can pick manually; never silently pick branches[0].
      toast.error('Location not available — please pick a store from the list.');
      setDetectingLocation(false);
      return;
    }
    setDetectingLocation(true);

    // We used to pre-check navigator.permissions.query({name:'geolocation'}) and
    // short-circuit on 'denied' to surface the right unblock instructions for
    // iOS Safari's cached per-site Deny case. WebKit's Permissions API has a
    // known false-positive (bug 237461) that returns 'denied' on a brand-new
    // device that has never been asked, blocking iOS from ever showing its
    // real system prompt. Just call getCurrentPosition directly — a real cached
    // deny returns code 1 immediately and the catch block below surfaces the
    // exact same getLocationBlockedMessage() toast, so removing the pre-check
    // has no functional downside.
    const toastId = 'detect-location';
    toast.locationStatus('Detecting nearest store...', { id: toastId });

    const resolve = async (position) => {
      const { latitude, longitude, accuracy } = position.coords;
      console.log('[Manual Detection] Coords:', { latitude, longitude, accuracy: Math.round(accuracy) + 'm' });
      setUserCoords({ latitude, longitude });

      if (!isWithinMMR(latitude, longitude)) {
        // Customer is outside Mumbai MMR. Flag outsideCity — the ChooseStoreHero
        // (already visible, no branch selected) renders its baked-in "not
        // delivering in your city yet" banner above the branch list so a
        // visiting customer can still pick a Mumbai branch. Do NOT silently pick
        // branches[0]. (The detect-location status toast is a no-op via
        // toastPolicy, so there's nothing to dismiss here.)
        setOutsideCity(true);
        return;
      }
      try {
        const res = await axios.get(`${API}/branches/nearest`, { params: { lat: latitude, lng: longitude } });
        if (res.data && res.data.id) {
          const match = branchList.find(b => b.id === res.data.id);
          if (match) {
            selectBranch(match);
            toast.locationStatus(`Nearest store: ${match.name} (${res.data.distance_km} km)`, { id: toastId, duration: 3000 });
            return;
          }
        }
      } catch (err) {
        console.error('[Manual Detection] API error:', err.message);
      }
      // Fallback: client-side haversine. Only pick when at least one branch
      // has coords — otherwise we have no signal and must defer to the customer
      // (Business Rule: branch selection is never silent).
      let nearest = null;
      let minDist = Infinity;
      branchList.forEach(b => {
        if (b.latitude && b.longitude) {
          const d = haversineKm(latitude, longitude, b.latitude, b.longitude);
          if (d < minDist) { minDist = d; nearest = b; }
        }
      });
      if (nearest) {
        selectBranch(nearest);
        toast.locationStatus(`Nearest store: ${nearest.name} (${minDist.toFixed(1)} km)`, { id: toastId, duration: 3000 });
      } else {
        // No branch has coords — defer to the customer. The ChooseStoreHero is
        // already on screen (no branch selected) so they can pick manually.
        toast.error('Could not detect nearest store — please pick one from the list.', { id: toastId });
      }
    };

    try {
      const position = await getCurrentPosition(GEO_DETECT_OPTS);
      await resolve(position);
    } catch (err) {
      console.warn('[Manual Detection] Failed:', err?.code, err?.message);
      if (err?.code === 1) {
        // PERMISSION_DENIED covers both a fresh "Don't Allow" tap and iOS Safari's
        // cached per-site Deny (where the system prompt never appears). Same fix
        // path either way — point the customer at the per-site setting. The
        // ChooseStoreHero stays visible for the manual pick (no branch selected).
        toast.error(getLocationBlockedMessage(), { id: toastId, duration: 7000 });
      } else {
        toast.error('Could not detect location. Please select a store manually.', { id: toastId });
      }
    } finally {
      // Detection concluded on every path — drop the detecting card so the
      // ChooseStoreHero (with its outside-city banner when applicable) shows.
      setDetectingLocation(false);
    }
  };

  // Fix 2 — resume re-detection. On foreground (the user backgrounded the app,
  // travelled, and reopened without killing it — iOS keeps it in memory, so no
  // cold-start detection fires), coarse-locate and, if a DIFFERENT branch is now
  // nearest, surface a switch suggestion modal. Never auto-switches (selectBranch
  // clears the cart). Respects an explicit manual pick and a per-session decline.
  const checkBranchOnResume = async () => {
    if (!isNativeApp() || !isGeolocationAvailable() || branches.length === 0) return;
    if (switchSuggestion) return; // a suggestion is already on screen
    if (sessionStorage.getItem('branchManuallySelected')) return; // respect explicit choice
    const current = getSavedBranchSync();
    if (!current?.id) return; // no branch yet — mount detection owns that path
    let position;
    try {
      position = await getCurrentPosition(GEO_DETECT_OPTS);
    } catch {
      return; // denied / unavailable / timeout — leave the current branch alone
    }
    const { latitude, longitude, accuracy } = position.coords;
    if (accuracy > MAX_DETECT_ACCURACY_M || !isWithinMMR(latitude, longitude)) return;
    let nearest = null;
    let distKm = null;
    try {
      const res = await axios.get(`${API}/branches/nearest`, { params: { lat: latitude, lng: longitude } });
      if (res.data?.id) { nearest = branches.find(b => b.id === res.data.id) || null; distKm = res.data.distance_km; }
    } catch { /* fall through to haversine */ }
    if (!nearest) {
      let minDist = Infinity;
      branches.forEach(b => {
        if (b.latitude && b.longitude) {
          const d = haversineKm(latitude, longitude, b.latitude, b.longitude);
          if (d < minDist) { minDist = d; nearest = b; }
        }
      });
      distKm = nearest ? Number(minDist.toFixed(1)) : null;
    }
    if (!nearest || nearest.id === current.id) return;      // already on the nearest
    if (dismissedSwitchRef.current.has(nearest.id)) return; // user declined this branch this session
    setSwitchSuggestion({ branch: nearest, distanceKm: distKm });
  };

  const confirmSwitchStore = () => {
    if (switchSuggestion?.branch) selectBranch(switchSuggestion.branch); // carries the cart across (Business Rule #50)
    setSwitchSuggestion(null);
  };

  const declineSwitchStore = () => {
    if (switchSuggestion?.branch) dismissedSwitchRef.current.add(switchSuggestion.branch.id);
    setSwitchSuggestion(null);
  };
  // Keep the ref pointed at the latest closure so the (once-bound) visibility
  // listener never calls a stale version (fresh `branches` / `switchSuggestion`).
  checkResumeRef.current = checkBranchOnResume;

  const selectBranch = (branch) => {
    if (branch === null) {
      // Explicit clear — used when geolocation fails / customer skips location.
      // The ChooseStoreHero state takes over until the customer picks explicitly.
      setSelectedBranch(null);
      localStorage.removeItem('selectedBranch');
      setShowBranchPicker(false);
      return;
    }
    if (!branch) return;
    // Business Rule #50 — switching store with items in the cart CARRIES the
    // cart across (re-mapped by parent_sku at the new store) instead of
    // clearing it. `switchBranch` writes localStorage synchronously before its
    // first await, so the branch is persisted by the time this returns; the
    // remap itself resolves in the background and the one-line summary
    // replaces the old "Cart cleared" toast.
    // The toast is decided from the switch result (`hadCart` reads the LIVE
    // cart), not this render's `cart`: GPS auto-detection calls this from a
    // closure created at mount, whose `cart` can be empty or long out of date.
    let prevId = null;
    try { prevId = JSON.parse(localStorage.getItem('selectedBranch') || 'null')?.id || null; } catch {}
    setSelectedBranch(branch);
    setShowBranchPicker(false);
    switchBranch(branch)
      .then((res) => { if (prevId && res?.changed && res?.hadCart) toast.info(buildSwitchSummary(res)); })
      .catch(() => {});
  };

  const fetchMenuItems = async (branchId) => {
    // SWR: on branch switch the lazy useState init can't help because state was
    // already populated with the previous branch's menu — read the cache here
    // so the swap is instant and the network call only refreshes.
    const cached = readCache(CACHE_KEYS.menu(branchId));
    if (cached) {
      setMenuItems(cached);
      setCategories([...new Set(cached.map((i) => i.category))]);
    }
    try {
      const res = await axios.get(`${API}/menu`, { params: { branch_id: branchId } });
      setMenuItems(res.data);
      const cats = [...new Set(res.data.map(i => i.category))];
      setCategories(cats);
      writeCache(CACHE_KEYS.menu(branchId), res.data);
    } catch {}
  };

  const fetchComboOffers = async () => {
    try {
      const data = (await axios.get(`${API}/combo-offers`)).data;
      setComboOffers(data);
      writeCache(CACHE_KEYS.comboOffers, data);
    } catch {}
  };

  const fetchSubscriptions = async () => {
    try {
      const data = (await axios.get(`${API}/subscriptions`)).data;
      setSubscriptions(data);
      writeCache(CACHE_KEYS.subscriptions, data);
    } catch {}
  };

  const fetchRecentOrders = async () => {
    try {
      const res = await axios.get(`${API}/user/recent-orders?limit=3`, { headers: { Authorization: `Bearer ${token}` } });
      setRecentOrders(res.data);
      if (user?.id && Array.isArray(res.data)) writeCache(CACHE_KEYS.recentOrders(user.id), res.data);
    } catch {}
  };

  const fetchUserSubscription = async () => {
    // Business Rule #23 rework (pt 7): the free-coffee tracker is hidden ONLY
    // while the customer holds an active pass whose admin `hide_free_coffee_tracker`
    // flag is set — passes without the flag leave the tracker visible. Read the
    // full active-pass array and check the hydrated `plan_hide_free_coffee_tracker`
    // mirror; a customer with no flagged pass keeps the tracker.
    try {
      const res = await axios.get(`${API}/user/subscriptions`, { headers: { Authorization: `Bearer ${token}` } });
      const passes = Array.isArray(res.data) ? res.data : [];
      setHideFreeCoffeeTracker(passes.some(p => p?.plan_hide_free_coffee_tracker));
    } catch { setHideFreeCoffeeTracker(false); }
  };

  const fetchCategoryImages = async () => {
    // Always refresh in the background — the lazy useState initializer may have
    // pre-filled state from cache (or `fetchBranches` from inline build-time
    // data), but we still want fresh URLs in case admin uploaded new category
    // images. Cache write keeps the next cold start instant.
    try {
      const res = await axios.get(`${API}/menu/categories/images`);
      setCategoryImages(res.data);
      writeCache(CACHE_KEYS.categoryImages, res.data);
    } catch {}
  };

  const handleReorder = async (orderId) => {
    trackDiscovery('home_reorder', '', selectedBranch?.id);
    setReorderLoading(orderId);
    try {
      const body = selectedBranch?.id ? { branch_id: selectedBranch.id } : {};
      const res = await axios.post(`${API}/user/reorder/${orderId}`, body, { headers: { Authorization: `Bearer ${token}` } });
      const added = res.data?.added ?? res.data?.items_count ?? 0;
      const skipped = Array.isArray(res.data?.skipped) ? res.data.skipped : [];
      await fetchCart();
      if (added === 0 && skipped.length > 0) {
        const branchLabel = selectedBranch?.name ? ` at ${selectedBranch.name}` : '';
        toast.error(`None of those items are available${branchLabel}`);
      } else if (skipped.length > 0) {
        const names = skipped.map(s => s.name).filter(Boolean).join(', ');
        const branchLabel = selectedBranch?.name ? ` at ${selectedBranch.name}` : '';
        toast.success(`Added ${added} ${added === 1 ? 'item' : 'items'} — ${names} not available${branchLabel}`);
        navigate('/cart');
      } else {
        toast.cartAdd('Items added to cart!');
        navigate('/cart');
      }
    } catch {
      toast.error('Failed to add items to cart');
    } finally { setReorderLoading(null); }
  };

  const handleAddComboToCart = async (combo) => {
    trackDiscovery('home_combo', combo?.name || '', selectedBranch?.id);
    if (!user) { toast.error('Please login first'); navigate('/login'); return; }
    try {
      await axios.post(`${API}/cart/add-combo`, { combo_id: combo.id }, { headers: { Authorization: `Bearer ${token}` } });
      toast.cartAdd(`${combo.name} added!`);
      await fetchCart();
      navigate('/checkout');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add combo'); }
  };

  const handleEnterFullSearch = (q) => {
    if (!q.trim()) return;
    trackDiscovery('home_search_full', q.trim(), selectedBranch?.id);
    navigate(`/menu?search=${encodeURIComponent(q.trim())}`);
  };

  const handleQuickAddToCart = async (item) => {
    lightHaptic(); // tap feedback on the "+" — same LIGHT tick as the nav tabs
    if (!user) { toast.error('Please login to add items'); navigate('/login'); return; }
    try {
      await addToCart({ menu_item_id: item.id, quantity: 1, customization: {}, customization_price: 0 });
      toast.cartAdd(`${item.name} added to cart!`);
    } catch {
      toast.error('Failed to add item');
    }
  };

  // Bestsellers: only items tagged as "Bestseller" by admin
  const bestsellers = menuItems.filter(i => i.available !== false && i.tags?.includes('Bestseller')).slice(0, 12);

  // Spotlight: "From the World" — the fixed international lineup, matched by
  // normalized name in FROM_THE_WORLD_LINEUP order (module scope). Entries
  // carry flag/origin for the country chips on the Espresso Atlas panel.
  const spotlightEntries = FROM_THE_WORLD_LINEUP
    .map(({ key, flag, origin }) => {
      const item = menuItems.find(i => i.available !== false && spotlightNameKey(i.name) === key);
      return item ? { item, flag, origin } : null;
    })
    .filter(Boolean);
  // Fallback to Cold Classics (plain section) if the branch carries none
  const spotlightDisplay = spotlightEntries.length > 0
    ? { world: true, entries: spotlightEntries }
    : {
        world: false,
        title: 'Cold Coffee Spotlight',
        subtitle: 'Signature cold brews to keep you refreshed.',
        entries: menuItems.filter(i => i.category === 'Cold Classics' && i.available !== false).slice(0, 8).map(item => ({ item })),
      };

  // Categories that actually exist for the current branch — own categories plus
  // app-only ones like "Signature Drinks" (Business Rule #62)
  const branchCategories = menuCategoryNames(menuItems.filter(i => i.available !== false));

  // Categories grid + quick-link rows follow the admin-arranged menu layout
  // (Products → Categories → Menu layout; one layout for every store). A
  // category the layout doesn't place still shows, at the end of the grid.
  const mainCategories = orderCategories(branchCategories, layoutSections);
  const quickRows = homeRowsFor(layoutHomeRows, branchCategories);
  // Festive theme flag (window decided server-side) — drives the header line + decor.
  const { seasonal_theme: seasonal } = useFeatureFlags();

  return (
    <div className="min-h-screen bg-background">
      {/* Ready-for-pickup QR popup — self-gates on /user/active-pickup (Business Rule #17). */}
      {user && <ReadyPickupPopup token={token} />}
      {/* Post-exhaustion renewal popup — self-gates on /user/active-renewal-prompt (Business Rule #23). */}
      {user && <RenewPassPopup token={token} />}
      {/* Launch pop-up, e.g. the Signature Drinks — guests too; waits 1.5 s and stands aside for any other Home pop-up (Business Rule #64). */}
      <LaunchPopup
        token={token}
        branch={selectedBranch}
        menuItems={menuItems}
        blocked={showLocationPrompt || reviewPromptOpen || reviewDetailOpen || showBranchPicker}
      />

      {/* Home-screen rating prompt + detailed feedback (Business Rule #19). */}
      {user && reviewOrder && (
        <>
          <ReviewPrompt
            order={reviewOrder}
            token={token}
            isOpen={reviewPromptOpen}
            onClose={(submitted, stars) => {
              setReviewPromptOpen(false);
              // Business Rule #37 — after the star is in, fold the taste questions
              // into the same moment by opening the detail sheet when any are due.
              if (submitted && personalizationAsk.length > 0) {
                setReviewDetailRating(stars || 0);
                setReviewDetailOpen(true);
              } else {
                fetchRecentOrders();
              }
            }}
            onOpenDetail={(stars) => {
              setReviewDetailRating(stars);
              setReviewPromptOpen(false);
              setReviewDetailOpen(true);
            }}
          />
          <ReviewDetailSheet
            order={reviewOrder}
            token={token}
            initialRating={reviewDetailRating}
            isOpen={reviewDetailOpen}
            askDimensions={personalizationAsk}
            consentRequired={personalizationConsent.required}
            consentVersion={personalizationConsent.version}
            onClose={() => {
              setReviewDetailOpen(false);
              setPersonalizationAsk([]);
              fetchRecentOrders();
            }}
          />
        </>
      )}

      {/* Location Permission Prompt */}
      {showLocationPrompt && (
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center px-6" data-testid="location-prompt-overlay">
          <div className="bg-card rounded-2xl shadow-2xl p-6 max-w-sm w-full animate-in fade-in zoom-in-95 duration-300" data-testid="location-prompt-modal">
            <div className="text-center mb-5">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <MapPin className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-lg font-bold mb-2">Find Your Nearest Store</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Allow location access so we can select the <strong>nearest Penguin Bean Coffee</strong> store for you. This ensures your order goes to the right branch.
              </p>
            </div>
            <div className="space-y-2.5">
              <Button
                className="w-full rounded-full h-11 font-semibold"
                onClick={handleAllowLocation}
                data-testid="allow-location-btn"
              >
                <MapPin className="w-4 h-4 mr-2" /> Allow Location
              </Button>
              <Button
                variant="ghost"
                className="w-full rounded-full h-11 text-muted-foreground"
                onClick={handleSkipLocation}
                data-testid="skip-location-btn"
              >
                Select Store Manually
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ——— Switch-store suggestion (Fix 2: resume re-detection) ——— */}
      {switchSuggestion && (
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center px-6" data-testid="switch-store-overlay">
          <div className="bg-card rounded-2xl shadow-2xl p-6 max-w-sm w-full animate-in fade-in zoom-in-95 duration-300" data-testid="switch-store-modal">
            <div className="text-center mb-5">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <MapPin className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-lg font-bold mb-2">You're near {switchSuggestion.branch.name}</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Looks like you've moved. Switch to <strong>{switchSuggestion.branch.name}</strong>
                {switchSuggestion.distanceKm != null ? ` (${switchSuggestion.distanceKm} km away)` : ''} so your order goes to the right branch?
              </p>
              {cart.length > 0 && (
                <p className="text-sm text-muted-foreground mt-2">We'll move your cart to {switchSuggestion.branch.name}. Anything they don't serve is removed.</p>
              )}
            </div>
            <div className="space-y-2.5">
              <Button
                className="w-full rounded-full h-11 font-semibold"
                onClick={confirmSwitchStore}
                data-testid="switch-store-confirm-btn"
              >
                <MapPin className="w-4 h-4 mr-2" /> Switch to {switchSuggestion.branch.name}
              </Button>
              <Button
                variant="ghost"
                className="w-full rounded-full h-11 text-muted-foreground"
                onClick={declineSwitchStore}
                data-testid="switch-store-decline-btn"
              >
                Stay on {selectedBranch?.name || 'current store'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ——— Header ——— */}
      <header className="sticky top-0 z-50 bg-primary text-primary-foreground">
        <div className="px-4 pt-2 pb-2 lg:max-w-3xl lg:mx-auto">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-white">
                <img src="/penguin-bean-logo.webp" alt="" width="40" height="40" className="w-full h-full scale-125 object-cover" />
              </div>
              <div className="min-w-0 flex-1">
                {/* Header layout A/B toggle (STORE_PROMINENT_HEADER):
                    - false (ORIGINAL, default): greeting bold on top, branch selector
                      as the small line below it.
                    - true (CURRENT): store/branch selector bold on top (with distance +
                      cold-start storeHighlight pulse), greeting as the small subtext below.
                    Both layouts share the SAME relative/branchRef/dropdown unit (no
                    duplication); only ordering + the button/greeting styling flips. */}
                {(() => {
                  const greeting = (
                    <p
                      className={STORE_PROMINENT_HEADER
                        ? 'text-xs text-primary-foreground/70 truncate mt-0.5'
                        : 'text-sm font-bold truncate'}
                      data-testid="welcome-text"
                    >
                      {seasonal?.active && seasonal.header_subtitle
                        ? (user ? `${seasonal.header_subtitle}, ${user.full_name || 'there'}` : seasonal.header_subtitle)
                        : user
                          ? `${STORE_PROMINENT_HEADER ? 'Hi' : 'Welcome,'} ${user.full_name || 'there'}`
                          : 'Penguin Bean Coffee'}
                    </p>
                  );
                  const storeBlock = (
                    <div className="relative" ref={branchRef}>
                      <button
                        onClick={() => setShowBranchPicker(!showBranchPicker)}
                        className={STORE_PROMINENT_HEADER
                          ? `flex items-center gap-1 max-w-full min-w-0 rounded-md -mx-1 px-1 transition-all duration-500 ${storeHighlight ? 'pb-store-attention' : ''}`
                          : 'flex items-center gap-1 text-xs text-primary-foreground/80 hover:text-primary-foreground max-w-full min-w-0'}
                        data-testid="branch-selector"
                      >
                        <MapPin className={STORE_PROMINENT_HEADER ? 'w-4 h-4 flex-shrink-0' : 'w-3 h-3 flex-shrink-0'} />
                        <span className={STORE_PROMINENT_HEADER ? 'text-sm font-semibold truncate' : 'truncate'}>
                          {selectedBranch ? selectedBranch.name : (STORE_PROMINENT_HEADER ? 'Select your store' : 'Select branch')}
                        </span>
                        {STORE_PROMINENT_HEADER && selectedBranch && headerDistanceKm != null && (
                          <span className="text-xs font-medium text-primary-foreground/70 flex-shrink-0">· {headerDistanceKm} km</span>
                        )}
                        <ChevronDown className={STORE_PROMINENT_HEADER ? 'w-3.5 h-3.5 flex-shrink-0' : 'w-3 h-3 flex-shrink-0'} />
                      </button>
                      {showBranchPicker && (
                        <div className="absolute top-6 left-0 bg-white text-foreground rounded-lg shadow-xl border p-2 min-w-[220px] z-50">
                          <button
                            onClick={() => { sessionStorage.removeItem('branchManuallySelected'); setShowBranchPicker(false); detectNearestBranch(branches); }}
                            className="w-full text-left px-3 py-2 rounded text-sm transition-colors hover:bg-blue-50 text-blue-600 flex items-center gap-2 border-b mb-1"
                            data-testid="detect-nearest-btn"
                          >
                            <MapPin className="w-3.5 h-3.5" /> Detect Nearest Store
                          </button>
                          {branches.map(b => {
                            const dist = userCoords && b.latitude && b.longitude
                              ? haversineKm(userCoords.latitude, userCoords.longitude, b.latitude, b.longitude)
                              : null;
                            return (
                            <button
                              key={b.id}
                              onClick={() => { sessionStorage.setItem('branchManuallySelected', 'true'); selectBranch(b); }}
                              className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${
                                selectedBranch?.id === b.id ? 'bg-primary/10 font-semibold' : 'hover:bg-muted'
                              }`}
                              data-testid={`branch-option-${b.id}`}
                            >
                              <span className="flex items-center justify-between">
                                <span className="font-medium">{b.name}</span>
                                {dist !== null && <span className="text-[10px] text-muted-foreground ml-2">{dist.toFixed(1)} km</span>}
                              </span>
                              {b.address && <span className="block text-[10px] text-muted-foreground truncate max-w-[200px]">{b.address}</span>}
                            </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                  return STORE_PROMINENT_HEADER
                    ? (<>{storeBlock}{greeting}</>)
                    : (<>{greeting}{storeBlock}</>);
                })()}
              </div>
            </div>
            {user ? (
              <div className="flex items-center gap-2 flex-shrink-0">
                <NotificationBell />
                <Link to="/profile" className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center overflow-hidden" data-testid="header-profile-btn">
                  <span className="text-sm font-bold text-white">{user.full_name?.charAt(0) || 'U'}</span>
                </Link>
              </div>
            ) : (
              <Link to="/login">
                <Button size="sm" variant="secondary" className="rounded-full text-xs h-8 px-4" data-testid="header-login-btn">
                  Login
                </Button>
              </Link>
            )}
          </div>

          {/* Search bar with live suggestions (≥3 chars) */}
          <div className="mt-3 mb-1">
            <MenuSearchAutocomplete
              query={searchQuery}
              onQueryChange={setSearchQuery}
              items={menuItems}
              onEnterFullSearch={handleEnterFullSearch}
              placeholder='Search for "Coffee"'
              testId="landing-search"
            />
          </div>
        </div>
      </header>

      {/* Desktop column: the body is a phone-first feed, so on a wide screen it
          sits in a centred 48rem column (same cap as ProductDetail / the header
          inner) instead of stretching the 16:9 hero across the whole window. */}
      <div className="lg:max-w-3xl lg:mx-auto" data-testid="home-column">

      {/* When no branch is selected, the entire branch-dependent body is
          replaced by one of two cards (Business Rule: branch selection is never
          silent — the app never falls back to branches[0]):
          (a) while the OS location dialog + GPS detection are still resolving
              (native first launch), a "Finding your nearest store…" DetectingCard
              so the picker doesn't flash up before the permission is answered;
          (b) otherwise the "Choose your store" hero — with a baked-in
              "not delivering in your city yet" banner above the list when the
              customer resolved outside Mumbai (replaces the old full-screen modal). */}
      {!selectedBranch ? (
        detectingLocation ? (
        <div className="px-4 py-10 flex justify-center" data-testid="detecting-location">
          <Card className="max-w-md w-full p-8 shadow-md text-center">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-accent/15 flex items-center justify-center">
              <Loader2 className="w-7 h-7 text-accent animate-spin" />
            </div>
            <h2 className="font-fraunces text-xl font-bold mb-1.5">Finding your nearest store…</h2>
            <p className="text-sm text-muted-foreground">Allow location to set up your nearest Penguin Bean.</p>
          </Card>
        </div>
        ) : (
        <div className="px-4 py-10 flex justify-center" data-testid="choose-store-hero">
          <Card className="max-w-md w-full p-6 shadow-md">
            {/* Baked-in outside-city banner (replaces the old full-screen
                "Not Delivering in Your City Yet" modal). */}
            {outsideCity && (
              <div className="mb-4 rounded-xl bg-amber-50 border border-amber-200 p-3 text-center" data-testid="outside-city-banner">
                <p className="text-sm font-semibold text-amber-800">Not delivering in your city yet</p>
                <p className="text-xs text-amber-700 mt-0.5">We currently serve only in Mumbai. Pick a store below to browse the menu.</p>
              </div>
            )}
            <div className="text-center mb-5">
              <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-accent/15 flex items-center justify-center">
                <MapPin className="w-7 h-7 text-accent" />
              </div>
              <h2 className="font-fraunces text-2xl font-bold mb-1.5">Choose your store</h2>
              <p className="text-sm text-muted-foreground">
                Pick the Penguin Bean nearest you to start ordering.
              </p>
            </div>

            {/* Directly-tappable branch list, sorted nearest-first when we have
                the customer's coords. Tapping a row selects that branch. */}
            <div className="space-y-2">
              {[...branches]
                .map(b => ({
                  b,
                  dist: userCoords && b.latitude && b.longitude
                    ? haversineKm(userCoords.latitude, userCoords.longitude, b.latitude, b.longitude)
                    : null,
                }))
                .sort((x, y) => {
                  if (x.dist === null && y.dist === null) return 0;
                  if (x.dist === null) return 1;
                  if (y.dist === null) return -1;
                  return x.dist - y.dist;
                })
                .map(({ b, dist }) => (
                  <button
                    key={b.id}
                    onClick={() => { sessionStorage.setItem('branchManuallySelected', 'true'); selectBranch(b); }}
                    className="w-full text-left px-4 py-3 rounded-xl border bg-card hover:bg-muted transition-colors flex items-center gap-3"
                    data-testid={`choose-store-branch-${b.id}`}
                  >
                    <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-4 h-4 text-primary" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold truncate">{b.name}</p>
                      {b.address && <p className="text-xs text-muted-foreground truncate">{b.address}</p>}
                    </div>
                    {dist !== null && (
                      <span className="text-xs font-medium text-muted-foreground flex-shrink-0">{dist.toFixed(1)} km</span>
                    )}
                  </button>
                ))}
            </div>

            {/* Divider + location CTA — never a blocking modal; the customer can
                always pick from the list above or retry location from here. On
                denial detectNearestBranch surfaces the platform-specific
                getLocationBlockedMessage() guidance. */}
            <div className="flex items-center gap-3 my-4">
              <div className="h-px bg-border flex-1" />
              <span className="text-[11px] uppercase tracking-wider text-muted-foreground">or</span>
              <div className="h-px bg-border flex-1" />
            </div>
            <Button
              variant="outline"
              className="w-full rounded-full h-11 font-semibold"
              onClick={() => detectNearestBranch(branches)}
              data-testid="choose-store-use-location"
            >
              <MapPin className="w-4 h-4 mr-2" /> Use my location
            </Button>

            <p className="text-xs text-muted-foreground mt-4 text-center">
              Already at a store? Scan the QR on the counter.
            </p>
          </Card>
        </div>
        )
      ) : (
      <>

      {/* ——— Quick Category Pills (Two Rows) — pb-3 + the last row's pb-1 = 16px
          above the hero banner (was 8px, owner ask 2026-10-21). ——— */}
      <section className="px-4 pt-3 pb-3 pb-festive-host" data-testid="quick-links">
        {/* Festive theme decor (components/FestiveDecor): all absolutely positioned,
            null outside the window — the rows below never move. */}
        <FestiveMandala seasonal={seasonal} />
        <FestiveLanterns seasonal={seasonal} />
        <FestiveGreeting seasonal={seasonal} />
        {quickRows.map((row, rowIndex) => (
          <div key={row.title} className={rowIndex < quickRows.length - 1 ? 'mb-3' : 'relative'}>
            {rowIndex === quickRows.length - 1 && row.links.length <= 4 && <FestiveMarigolds seasonal={seasonal} snug={row.links.length === 4} />}
            <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">{row.title}</p>
            <div className="flex gap-3 overflow-x-auto pb-1 scrollbar-hide">
              {row.links.map(({ label, icon, category: cat }) => (
                <Link key={cat} to={`/menu?category=${encodeURIComponent(cat)}`} onClick={() => trackDiscovery('home_quick_pill', cat, selectedBranch?.id)} className="flex flex-col items-center gap-1.5 flex-shrink-0" data-testid={`quick-link-${cat}`}>
                  <div className={`w-14 h-14 rounded-full ${rowIndex === 0 ? 'bg-primary' : 'bg-coffee'} flex items-center justify-center text-xl shadow-md hover:scale-105 transition-transform overflow-hidden`}>
                    {categoryImages[cat] ? (
                      <OptimizedImage
                        src={categoryImages[cat]}
                        imageUrls={categoryImages[`${cat}__urls`] ? categoryImages[`${cat}__urls`] : undefined}
                        size="thumb"
                        alt={label}
                        className="w-full h-full"
                        width={56}
                        height={56}
                        fallbackIcon={false}
                      />
                    ) : icon}
                  </div>
                  {/* Wraps to two lines ("Signature / Drinks") instead of cutting off;
                      w-16 -mx-1 gives the text 64px under the 56px circle without
                      moving the circles. */}
                  <span className="text-[10px] font-medium text-center w-16 -mx-1 leading-tight line-clamp-2">{label}</span>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </section>

      {/* ——— Store closed / not operational banner (Business Rule #38) ——— */}
      {storeClosed && (
        <section className="px-4">
          <div className="mb-4 rounded-2xl border border-amber-300 bg-amber-50 p-4" data-testid="landing-store-closed-banner">
            <p className="text-sm font-bold text-amber-900">
              {storeState?.reason === 'not_accepting' ? 'Not taking orders right now' : 'Currently closed'}
            </p>
            <p className="text-xs text-amber-800 mt-1">
              {storeState?.reason === 'not_accepting'
                ? 'We are not operational right now. We will be serving soon.'
                : (storeState?.message || 'Please check back during our operating hours.')}
            </p>
          </div>
        </section>
      )}

      {/* ——— Hero carousel banner (admin-managed) ——— */}
      <section className="px-4" data-testid="banner-zone-home-hero">
        <BannerSlot zone="home_hero_carousel" branchId={selectedBranch?.id || ''} className="mb-4" />
      </section>

      {/* ——— Tiffin of the Day (Business Rule #58) — a slim bar directly under
          the hero banner (moved from above it, owner ask 2026-10-21). Renders
          nothing (no gap) at a store without the plan, on an empty day, while
          the store is closed or the partner kitchen is outside its hours. ——— */}
      <TiffinOfTheDayBar
        branch={selectedBranch}
        storeState={storeState}
        menuItems={menuItems}
        isAdmin={!!user?.is_admin}
      />

      {/* ——— Welcome Coffee strip (Business Rule #45) — the app-install reward.
          Sits ABOVE the loyalty tracker because a fresh installer's tracker is
          empty; this is the reward they can act on today. Self-fetching and
          renders nothing unless an entitlement is open, so it costs a logged-out
          visitor nothing. ——— */}
      {user && (
        <section className="px-4 mb-4">
          <WelcomeCoffeeCard compact />
        </section>
      )}

      {/* ——— Free Coffee Tracker — shows a pre-login teaser when logged out;
          hidden ONLY while the customer holds an active pass whose admin
          hide_free_coffee_tracker flag is set (Business Rule #23 rework, pt 7).
          A pass without that flag leaves the tracker visible. ——— */}
      {!hideFreeCoffeeTracker && (
        <section className="px-4 mb-4">
          <FreeCoffeeProgress />
        </section>
      )}

      {/* ——— Combo Offers Banner ——— */}
      {comboOffers.length > 0 && (
        <section className="px-4 mb-4" data-testid="combo-offers-section">
          <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
            {comboOffers.map(offer => (
              <div
                key={offer.id}
                className="flex-shrink-0 w-[85vw] max-w-[360px] rounded-2xl overflow-hidden relative cursor-pointer"
                onClick={() => handleAddComboToCart(offer)}
                data-testid={`combo-banner-${offer.id}`}
              >
                {offer.image_url ? (
                  <img src={offer.image_url} alt={offer.title} width="360" height="176" className="w-full h-44 object-cover" loading="lazy" />
                ) : (
                  <div className="w-full h-44 bg-gradient-to-br from-primary to-accent flex items-center justify-center p-4">
                    <div className="text-white text-center">
                      <p className="text-2xl font-bold font-fraunces">{offer.title || offer.name}</p>
                      <p className="text-sm mt-1 opacity-90">{offer.description}</p>
                    </div>
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                  <div className="flex items-end justify-between">
                    <div>
                      <p className="text-white font-bold text-sm">{offer.title || offer.name}</p>
                      <p className="text-white/80 text-xs">{offer.description}</p>
                    </div>
                    <Badge className="bg-green-500 text-white text-xs">
                      Save ₹{(offer.original_price - offer.offer_price).toFixed(0)}
                    </Badge>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ——— Recent Orders (logged in) ——— */}
      {user && recentOrders.length > 0 && (
        <section className="px-4 mb-4" data-testid="recent-orders-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-bold">Order Again</h2>
            <Link to="/orders" className="text-xs text-primary font-medium">View All</Link>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
            {recentOrders.map((order, idx) => (
              <Card key={order.id} className="flex-shrink-0 w-60 max-w-[80vw] p-3" data-testid={`recent-order-${idx}`}>
                <div className="flex items-center gap-1.5 mb-2">
                  <Clock className="w-3 h-3 text-muted-foreground" />
                  <span className="text-[10px] text-muted-foreground">
                    {new Date(order.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                  </span>
                  <Badge variant="outline" className="ml-auto text-[10px] px-1.5 py-0">₹{order.total_amount?.toFixed(0)}</Badge>
                </div>
                <div className="flex gap-1.5 mb-2.5">
                  {order.items?.slice(0, 3).map((item, i) => (
                    <div key={i} className="w-12 h-12 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                      <OptimizedImage
                        src={item.image_url}
                        imageUrls={item.image_urls}
                        size="thumb"
                        alt={item.name}
                        className="w-12 h-12 rounded-lg"
                        width={48}
                        height={48}
                      />
                    </div>
                  ))}
                  {order.items?.length > 3 && (
                    <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                      <span className="text-[10px] text-muted-foreground">+{order.items.length - 3}</span>
                    </div>
                  )}
                </div>
                <p className="text-xs font-medium leading-tight line-clamp-2 mb-2.5">
                  {order.items?.map(item => item.name).join(', ')}
                </p>
                <Button
                  size="sm"
                  className="w-full rounded-full h-7 text-xs"
                  onClick={() => handleReorder(order.id)}
                  disabled={reorderLoading === order.id}
                  data-testid={`reorder-btn-${idx}`}
                >
                  {reorderLoading === order.id ? 'Adding...' : 'Reorder'}
                </Button>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* ——— Mid-strip banner (admin-managed, above Bestsellers) ——— */}
      <section className="px-4" data-testid="banner-zone-home-strip">
        <BannerSlot zone="home_strip" branchId={selectedBranch?.id || ''} className="mb-4" />
      </section>

      {/* Loading skeleton for the menu-dependent middle (Bestsellers + Spotlight
          + Categories). Renders the moment React mounts on a cold start when
          the SWR cache (utils/menuCache) had no hit — picks up where the build-
          time prerender shell at scripts/prerender-shell.js left off so the
          middle of the page never goes blank between React mount and network
          resolve. The real sections below all gate on their own `.length > 0`
          so they appear automatically the moment menu data lands. */}
      {selectedBranch && menuItems.length === 0 && <LandingMenuSkeleton />}

      {/* ——— Bestsellers ——— */}
      {bestsellers.length > 0 && (
        <section className="px-4 mb-6" data-testid="bestsellers-section">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-base font-bold">Bestsellers!</h2>
              <p className="text-xs text-muted-foreground">Fresh. Fast. Flavourful.</p>
            </div>
            <Link to="/menu" onClick={() => trackDiscovery('home_view_all', '', selectedBranch?.id)} className="text-xs text-primary font-medium">View All</Link>
          </div>
          {/* md+ is 4 across: hide a trailing part-row (1–3 orphans) so the grid
              ends on a full row — the first orphan is the card at position 4n+1
              among the last three, and every sibling after it goes with it. A
              grid of ≤3 cards keeps them all (:not(:first-child)). "View All"
              still reaches every bestseller. */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:[&>a:nth-child(4n+1):nth-last-child(-n+3):not(:first-child)]:hidden md:[&>a:nth-child(4n+1):nth-last-child(-n+3):not(:first-child)~a]:hidden">
            {bestsellers.map((item, idx) => (
              <Link
                key={item.id}
                to={`/menu/item/${item.id}`}
                state={{ backgroundLocation: location }}
                onClick={() => trackDiscovery('home_bestseller', item.name, selectedBranch?.id)}
                className="group"
                data-testid={`bestseller-${item.id}`}
              >
                <Card className="overflow-hidden h-full">
                  <div className="aspect-square relative overflow-hidden bg-muted">
                    <OptimizedImage
                      src={item.image_url}
                      imageUrls={item.image_urls}
                      size="medium"
                      sizes="(max-width: 640px) 50vw, (max-width: 768px) 33vw, 25vw"
                      alt={item.name}
                      className="w-full h-full"
                      width={400}
                      height={400}
                      priority={idx === 0}
                    />
                    <div className="absolute bottom-1.5 right-1.5">
                      <div className="w-7 h-7 rounded-full bg-accent text-white flex items-center justify-center shadow-md">
                        <Plus className="w-4 h-4" />
                      </div>
                    </div>
                  </div>
                  <div className="p-2.5">
                    <p className="text-xs font-semibold line-clamp-2 leading-tight">{item.name}</p>
                    {item.item_discount ? (
                      <div className="flex items-center gap-1 mt-1">
                        <p className="text-xs font-bold text-green-600">
                          ₹{item.item_discount.discount_type === 'percentage'
                            ? Math.round(item.price * (1 - item.item_discount.discount_value / 100))
                            : Math.max(0, item.price - item.item_discount.discount_value)}
                        </p>
                        <p className="text-[10px] text-muted-foreground line-through">₹{item.price}</p>
                        <span className="text-[8px] bg-green-100 text-green-700 px-1 py-0.5 rounded-full font-semibold">
                          {item.item_discount.discount_type === 'percentage' ? `${item.item_discount.discount_value}% OFF` : `₹${item.item_discount.discount_value} OFF`}
                        </span>
                      </div>
                    ) : (
                      <p className="text-xs font-bold text-foreground mt-1">₹{item.price}</p>
                    )}
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* ——— Spotlight Section ——— */}
      {spotlightDisplay.entries.length > 0 && (spotlightDisplay.world ? (
        /* "From the World" — Espresso Atlas panel (dark, globe + flight route) */
        <section className="px-2.5 mb-6" data-testid="spotlight-section">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[hsl(25_40%_19%)] via-[hsl(25_42%_12%)] to-[hsl(25_45%_9%)] pt-5">
            <WorldGlobeSketch />
            <WorldRouteSketch />
            <div className="relative px-4 pb-3.5">
              <h2 className="font-fraunces text-[26px] font-semibold leading-[1.05] tracking-tight text-[hsl(35_45%_92%)]">
                From the <em className="italic text-accent">World</em>
              </h2>
              <p className="mt-1.5 max-w-[270px] text-xs text-[hsl(35_30%_78%)]/85">
                Global coffee classics, brewed the Penguin Bean way.
              </p>
            </div>
            <div className="relative flex gap-3 overflow-x-auto px-4 pb-4 pt-1 scrollbar-hide">
              {spotlightDisplay.entries.map(entry => (
                <SpotlightCard key={entry.item.id} entry={entry} location={location} world onTap={() => trackDiscovery('home_spotlight', entry.item.name, selectedBranch?.id)} />
              ))}
            </div>
          </div>
        </section>
      ) : (
        /* Cold Classics fallback — plain section */
        <section className="px-4 mb-6" data-testid="spotlight-section">
          <div className="mb-3">
            <h2 className="text-base font-bold">{spotlightDisplay.title}</h2>
            <p className="text-xs text-muted-foreground">{spotlightDisplay.subtitle}</p>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
            {spotlightDisplay.entries.map(entry => (
              <SpotlightCard key={entry.item.id} entry={entry} location={location} onTap={() => trackDiscovery('home_spotlight', entry.item.name, selectedBranch?.id)} />
            ))}
          </div>
        </section>
      ))}

      {/* ——— Pre-categories carousel banner (admin-managed) ——— */}
      <section className="px-4" data-testid="banner-zone-home-pre-categories">
        <BannerSlot zone="home_pre_categories" branchId={selectedBranch?.id || ''} className="mb-4" />
      </section>

      {/* ——— Categories Grid ——— */}
      {mainCategories.length > 0 && (
        <section className="px-4 mb-6" data-testid="categories-grid-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-bold">Categories</h2>
            <Link to="/menu" onClick={() => trackDiscovery('home_view_all', '', selectedBranch?.id)} className="text-xs text-primary font-medium">View All</Link>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {mainCategories.map(cat => {
              const imgUrl = categoryImages[cat];
              return (
                <Link
                  key={cat}
                  to={`/menu?category=${encodeURIComponent(cat)}`}
                  onClick={() => trackDiscovery('home_category_tile', cat, selectedBranch?.id)}
                  className="group"
                  data-testid={`cat-grid-${cat}`}
                >
                  <div className="aspect-square rounded-2xl overflow-hidden bg-muted shadow-sm group-hover:shadow-md transition-shadow">
                    <OptimizedImage
                      src={imgUrl}
                      imageUrls={categoryImages[`${cat}__urls`] ? categoryImages[`${cat}__urls`] : undefined}
                      size="thumb"
                      alt={cat}
                      className="w-full h-full"
                      width={120}
                      height={120}
                      eager
                    />
                  </div>
                  <p className="text-[11px] font-medium mt-1.5 text-center line-clamp-2 leading-tight">{cat}</p>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* ——— Footer CTA banner (admin-managed, above footer) ——— */}
      <section className="px-4" data-testid="banner-zone-home-footer-cta">
        <BannerSlot zone="home_footer_cta" branchId={selectedBranch?.id || ''} className="mb-4" />
      </section>

      </>
      )}

      </div>

      {/* Bottom clearance: ~pb-32 (8rem) clears the BottomNav (~5.5rem) + Menu
          FAB (~6rem bottom offset) so the last visible section (BannerSlot or
          ChooseStoreHero) is fully scrollable above the fixed UI on every
          viewport. Footer moved to Profile — see Profile.js. */}
      <div className="h-32" aria-hidden="true" data-testid="home-bottom-spacer" />

      {/* ——— Floating Menu Button ——— */}
      <FloatingMenuButton menuItems={menuItems} />

      {/* ——— Bottom Navigation ——— */}
    </div>
  );
}
